import { useCallback } from 'react';

import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { TeaserVariant } from '@/shared/types/teaser';
import { errorMessage } from '@/util/validation/helpers';
import { integrationRulesWebWidget } from '@/util/validation/rules/integration-rules';

import QuickReplies from './QuickReplies';
import { TeaserFieldsPrefix, TeaserHostForm } from './types';

type Props = { prefix: TeaserFieldsPrefix; readOnly?: boolean };

/**
 * The variant picker and the fields that variant owns, at whichever level of
 * the teaser it is used.
 *
 * The picker owns the field set: the orchestrator rejects a field the chosen
 * variant does not own, so a form that shows `placeholder` on a `callout` can
 * produce a save error on a field the author cannot see. Rendering only the
 * owned fields is half of that; `toTeaserBlock` dropping the unowned ones is
 * the other half.
 */
const TeaserFields = ({ prefix, readOnly }: Props) => {
  const { t } = useTranslation();
  const { control, clearErrors } = useFormContext<TeaserHostForm>();

  const variant = useWatch({ control, name: `${prefix}.variant` });

  /**
   * `required: false` is not enough on its own. A field that is no longer
   * rendered keeps whatever error it already had, and `handleSubmit` refuses to
   * run while any error stands — so switching variant would silently block the
   * save with no visible cause.
   *
   * `clearErrors` rather than `unregister`: unregistering discards what the
   * author typed, so flipping to another variant and back would lose their
   * copy. The values stay resident and the serialiser drops the unowned ones.
   */
  const handleVariantChange = useCallback(
    (onChange: (value: TeaserVariant) => void) => (value: string) => {
      onChange(value as TeaserVariant);
      clearErrors([`${prefix}.message`, `${prefix}.placeholder`, `${prefix}.quick_replies`]);
    },
    [clearErrors, prefix]
  );

  const showsMessage = variant === 'callout' || variant === 'composer' || !variant;
  const showsPlaceholder = variant === 'composer';
  const showsQuickReplies = variant === 'question_chips';

  // Unique per level, so the same fields authored at the block, a page and a
  // section each get their own label association.
  const fieldId = (name: string) => `${prefix}-${name}`;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <Label htmlFor={fieldId('variant')} className="text-sm leading-5 font-medium">
          {t('integrations.web_teaser.variant.title')}
        </Label>
        <Controller
          control={control}
          name={`${prefix}.variant`}
          render={({ field: { onChange, value } }) => (
            <Select
              value={value ?? 'callout'}
              onValueChange={handleVariantChange(onChange)}
              disabled={readOnly}
            >
              <SelectTrigger id={fieldId('variant')} className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="callout">
                  {t('integrations.web_teaser.variant.callout')}
                </SelectItem>
                <SelectItem value="question_chips">
                  {t('integrations.web_teaser.variant.question_chips')}
                </SelectItem>
                <SelectItem value="composer">
                  {t('integrations.web_teaser.variant.composer')}
                </SelectItem>
                <SelectItem value="none">{t('integrations.web_teaser.variant.none')}</SelectItem>
              </SelectContent>
            </Select>
          )}
        />
        <p className="text-sm leading-5 text-muted-foreground">
          {t('integrations.web_teaser.variant.help')}
        </p>
      </div>

      {showsMessage && (
        <div className="flex flex-col gap-2">
          <Label
            htmlFor={fieldId('message')}
            className="text-sm leading-5 font-semibold text-muted-foreground"
          >
            {t('integrations.web_teaser.message.title')}
          </Label>
          {/* Not required: `{ variant: 'callout' }` with no message is valid on the wire. */}
          <Controller
            control={control}
            name={`${prefix}.message`}
            rules={integrationRulesWebWidget.welcomeMessage}
            render={({ field, fieldState: { error } }) => (
              <>
                <Input
                  id={fieldId('message')}
                  placeholder={t('integrations.web_teaser.message.placeholder')}
                  readOnly={readOnly}
                  aria-invalid={!!error}
                  {...field}
                  value={field.value ?? ''}
                />
                {error && (
                  <p className="text-sm leading-5 text-warning">
                    {errorMessage({
                      field: error,
                      maxLength: integrationRulesWebWidget.welcomeMessage.maxLength,
                    })}
                  </p>
                )}
              </>
            )}
          />
        </div>
      )}

      {showsPlaceholder && (
        <div className="flex flex-col gap-2">
          <Label
            htmlFor={fieldId('placeholder')}
            className="text-sm leading-5 font-semibold text-muted-foreground"
          >
            {t('integrations.web_teaser.input_placeholder.title')}
          </Label>
          <Controller
            control={control}
            name={`${prefix}.placeholder`}
            render={({ field }) => (
              <Input
                id={fieldId('placeholder')}
                placeholder={t('integrations.web_teaser.input_placeholder.placeholder')}
                readOnly={readOnly}
                {...field}
                value={field.value ?? ''}
              />
            )}
          />
        </div>
      )}

      {showsQuickReplies && <QuickReplies prefix={prefix} readOnly={readOnly} required />}
    </div>
  );
};

export default TeaserFields;
